import {
  TransitionDto,
  TransitionKind,
  TransitionSpeed,
} from "@repo/api/transition";
import type { CSSProperties, FC } from "react";
import { msToFrames } from "../../utils/timing.ts";
import { TransitionFormConfig } from "../ImageTransitionBuilder/ImageTransitionBuilder.ts";
import { CompositionNoneTransitionBuilder } from "./CompositionNoneTransitionBuilder.ts";
import {
  EndFadeBlackTransitionBuilder,
  StartFadeBlackTransitionBuilder,
} from "./CompositionFadeBlackTransitionBuilder.tsx";
import {
  EndFadeWhiteTransitionBuilder,
  StartFadeWhiteTransitionBuilder,
} from "./CompositionFadeWhiteTransitionBuilder.tsx";

export type CompositionTransitionType = "START" | "END";

export interface CompositionTransitionBuilderProps {
  transition: TransitionDto;
  fps: number;
}

export interface CompositionTransitionWrapperProps extends CompositionTransitionBuilderProps {
  durationInFrames: number;
  currentFrame: number;
}

export interface CompositionWrapper {
  overlayComponent?: FC<CompositionTransitionWrapperProps>;
  buildWrapperStyle?: (
    props: CompositionTransitionWrapperProps,
  ) => CSSProperties | null;
}

export interface CompositionTransitionBuilder {
  formConfig: TransitionFormConfig;
  wrapper?: CompositionWrapper;
  getPreviewClipDurationInFrames: (
    props: CompositionTransitionBuilderProps,
  ) => number;
}

export function getDefaultPreviewClipDurationInFrames(
  props: CompositionTransitionBuilderProps,
): number {
  const speed = props.transition.speed;
  if (!speed) {
    throw new Error(
      "Global transition speed is required when using getDefaultPreviewClipDurationInFrames",
    );
  }

  if (speed === "SLOW") {
    return msToFrames(2400, props.fps);
  } else if (speed === "NORMAL") {
    return msToFrames(2000, props.fps);
  } else if (speed === "FAST") {
    return msToFrames(1800, props.fps);
  } else {
    throw new Error(`Unknown global transition speed: ${speed}`);
  }
}

export const COMPOSITION_FADE_TRANSITION_DURATIONS_MS: Record<
  TransitionSpeed,
  number
> = {
  SLOW: 2000,
  NORMAL: 1000,
  FAST: 500,
};

export function getStartTransitionDurationMs(
  transition: TransitionDto | undefined,
): number {
  if (
    !transition?.speed ||
    (transition.kind !== "FADE_BLACK" && transition.kind !== "FADE_WHITE")
  ) {
    return 0;
  }
  return COMPOSITION_FADE_TRANSITION_DURATIONS_MS[transition.speed];
}

const StartTransitionBuilders: Map<
  TransitionKind,
  CompositionTransitionBuilder
> = new Map([
  [TransitionKind.enum.NONE, CompositionNoneTransitionBuilder],
  [TransitionKind.enum.FADE_BLACK, StartFadeBlackTransitionBuilder],
  [TransitionKind.enum.FADE_WHITE, StartFadeWhiteTransitionBuilder],
]);

const EndTransitionBuilders: Map<TransitionKind, CompositionTransitionBuilder> =
  new Map([
    [TransitionKind.enum.NONE, CompositionNoneTransitionBuilder],
    [TransitionKind.enum.FADE_BLACK, EndFadeBlackTransitionBuilder],
    [TransitionKind.enum.FADE_WHITE, EndFadeWhiteTransitionBuilder],
  ]);

export const getCompositionTransitionBuilder = (
  type: CompositionTransitionType,
  kind: TransitionKind,
) => {
  const transitionBuilders =
    type === "START" ? StartTransitionBuilders : EndTransitionBuilders;

  const transitionBuilder = transitionBuilders.get(kind);

  if (!transitionBuilder) {
    throw new Error(`CompositionTransitionBuilder not found for kind: ${kind}`);
  }

  return transitionBuilder;
};

export const getCompositionTransitionWrapper = (
  type: CompositionTransitionType,
  kind: TransitionKind,
) => {
  const transitionBuilders =
    type === "START" ? StartTransitionBuilders : EndTransitionBuilders;

  return transitionBuilders.get(kind)?.wrapper ?? null;
};
